import { BonusCondition, ConditionMode, Currency, getUnitTranslationId } from '@synergy/models';
import { FormattedNumber, useIntl } from 'react-intl';
import { ModeFormattedNumber, PoolFormikNumberTextfield } from '@synergy/components-next';
import { TableCell, styled } from '@mui/material';
import { getCurrencySymbol } from '@synergy/utils';

interface BonusConditionTableCellValueProps {
    rowIndex: number;
    bonusConditionValueIndex: number;
    readOnly: boolean;
    bonusCondition: BonusCondition;
    defaultCurrency: Currency;
    handleChange: (value: string | null) => void;
    revenue?: number | null;
    locale: string;
    value: number;
}

const RevenueCell = styled(TableCell)(({ theme }) => ({
    borderRight: `1px solid ${theme.palette.grey[700]}`,
    maxWidth: 140,
    textAlign: 'right',
}));

export const BonusConditionTableCellValue = ({
    readOnly,
    rowIndex,
    bonusConditionValueIndex,
    bonusCondition,
    handleChange,
    defaultCurrency,
    locale,
    value,
    revenue,
}: BonusConditionTableCellValueProps) => {
    const { mode, unit, currency } = bonusCondition;
    const intl = useIntl();
    const currencySymbol = getCurrencySymbol(currency || defaultCurrency, locale);
    const suffixUnit =
        mode === ConditionMode.Amount && unit ? intl.formatMessage({ id: getUnitTranslationId(unit) }) : undefined;

    let revenueColumn = undefined;

    if (mode === ConditionMode.Amount) {
        if (!readOnly) {
            revenueColumn = (
                <RevenueCell>
                    <PoolFormikNumberTextfield
                        name={`customerBonusSettlements.${rowIndex}.bonusConditionValues.${bonusConditionValueIndex}.revenue`}
                        textAlign="right"
                        suffix={currencySymbol}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        fullWidth={true}
                    />
                </RevenueCell>
            );
        } else if (revenue !== null && revenue !== undefined) {
            revenueColumn = (
                <RevenueCell>
                    <FormattedNumber value={revenue} currency={currency} style="currency" maximumFractionDigits={2} />
                </RevenueCell>
            );
        } else {
            revenueColumn = <RevenueCell />;
        }
    }

    return (
        <>
            <TableCell sx={{ minWidth: 70, maxWidth: 150, textAlign: 'right' }}>
                {readOnly ? (
                    <ModeFormattedNumber mode={mode} value={value} currency={currency ?? defaultCurrency} unit={unit} />
                ) : (
                    <PoolFormikNumberTextfield
                        name={`customerBonusSettlements.${rowIndex}.bonusConditionValues.${bonusConditionValueIndex}.value`}
                        textAlign="right"
                        placeholder={intl.formatMessage(
                            {
                                id: 'app.bonusSettlements.supplierDetail.conditionValuePlaceholder',
                            },
                            { conditionMode: mode }
                        )}
                        suffix={` ${suffixUnit ?? currencySymbol}`}
                        onValueChange={value => handleChange(value)}
                        fixedDecimalScale={mode === ConditionMode.Percentage}
                        decimalScale={mode === ConditionMode.Percentage ? 2 : undefined}
                        fullWidth={true}
                    />
                )}
            </TableCell>
            {revenueColumn}
        </>
    );
};
